/**
 * P3.3.a · SPA cost badge · session-level token + USD aggregate
 *
 * Polls `GET /api/sessions/:id/cost` and renders compact horizontal badge.
 * 设计原则（per CLAUDE.md rule 2）· 走 design-system tokens · 零硬编码色。
 *
 * Display format（v0.1 用户 2026-04-28 拍板）：
 *
 *   ┌─ tokens · 1.2k in / 850 out · ~$0.013 (estimated) ─┐
 *
 * v0.1 不显精确 USD · 一律 `~$X.XXX (estimated)` 角标。其他 model 后端
 * 给 usd=null → SPA 隐藏 USD 部分 · 只显 tokens（claude-3-opus 才有
 * estimate · 见 packages/util/src/cost-constants.ts SUPPORTED_MODELS）。
 *
 * Polling cadence v0.1: 5s interval · v0.2 升级为 audit event 触发
 * invalidation（WorkspaceBus task_run.finished → refetch）以减轻 D1 负担。
 */

import { useEffect, useState } from 'react';
import { fetchSessionCost, type SessionCost } from '../lib/fetch-session-cost';

export interface CostBadgeProps {
  sessionId: string;
  backendUrl: string;
  /** SPA JWT · null 时整组件不渲染（未登录） */
  authToken: string | null;
  /** Test-only · 注 mock fetcher。 */
  fetcherForTest?: typeof fetchSessionCost;
  /** Poll 间隔毫秒 · 默认 5000 · 0 关闭 polling（手动 refresh）。 */
  pollMs?: number;
}

export function CostBadge({
  sessionId,
  backendUrl,
  authToken,
  fetcherForTest,
  pollMs = 5000,
}: CostBadgeProps): JSX.Element | null {
  const [cost, setCost] = useState<SessionCost | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authToken) return;
    let cancelled = false;
    const fetcher = fetcherForTest ?? fetchSessionCost;
    const tick = async (): Promise<void> => {
      try {
        const c = await fetcher(backendUrl, authToken, sessionId);
        if (!cancelled) {
          setCost(c);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      }
    };
    void tick();
    if (pollMs > 0) {
      const t = setInterval(tick, pollMs);
      return () => {
        cancelled = true;
        clearInterval(t);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [sessionId, backendUrl, authToken, pollMs, fetcherForTest]);

  if (!authToken) return null;
  if (error) {
    return (
      <div data-testid="cost-badge" style={badgeStyle} aria-label="cost · 加载失败">
        <span style={labelStyle}>cost</span>
        <span style={errorStyle}>加载失败</span>
      </div>
    );
  }
  if (!cost) {
    return (
      <div data-testid="cost-badge" style={badgeStyle} aria-label="cost · 加载中">
        <span style={labelStyle}>cost</span>
        <span style={loadingStyle}>…</span>
      </div>
    );
  }

  const tokensTotal = cost.total_tokens_in + cost.total_tokens_out;
  const usd = cost.total_cost_usd_micros / 1_000_000;
  // claude-3-opus 才有 USD 估算 · 其他 model usd=0 (backend 返 null → micros 兜 0)
  const showUsd = cost.total_cost_usd_micros > 0;

  return (
    <div data-testid="cost-badge" style={badgeStyle} aria-label="session cost">
      <span style={labelStyle}>cost</span>
      <span data-testid="cost-tokens" style={valueStyle}>
        {formatTokens(tokensTotal)} tokens
      </span>
      <span style={separatorStyle}>·</span>
      <span data-testid="cost-tokens-detail" style={detailStyle}>
        {formatTokens(cost.total_tokens_in)} in / {formatTokens(cost.total_tokens_out)} out
      </span>
      {showUsd ? (
        <>
          <span style={separatorStyle}>·</span>
          <span data-testid="cost-usd" style={valueStyle} title="v0.1 全部 estimated · 见 tech-stack.md §9.3">
            ~${usd.toFixed(usd < 0.01 ? 4 : 3)}
          </span>
          <span style={estimatedStyle}>(estimated)</span>
        </>
      ) : null}
      <span style={separatorStyle}>·</span>
      <span data-testid="cost-runs" style={detailStyle}>
        {cost.run_count} run{cost.run_count === 1 ? '' : 's'}
      </span>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

import type { CSSProperties } from 'react';

const badgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  fontFamily: 'var(--font-body)',
  fontSize: 11,
  color: 'var(--fg-default)',
  whiteSpace: 'nowrap',
};

const labelStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  color: 'var(--fg-faint)',
  textTransform: 'uppercase',
  fontSize: 10,
  letterSpacing: 0.5,
};

const valueStyle: CSSProperties = {
  fontWeight: 600,
};

const detailStyle: CSSProperties = {
  color: 'var(--fg-soft)',
};

const separatorStyle: CSSProperties = {
  color: 'var(--fg-faint)',
};

const estimatedStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--fg-faint)',
  fontStyle: 'italic',
};

const errorStyle: CSSProperties = {
  color: 'var(--accent-danger)',
  fontSize: 10,
};

const loadingStyle: CSSProperties = {
  color: 'var(--fg-faint)',
};
